import { createLazyResource, createMutation, createTriggerResource, toast, useLocalization } from '@magicdoorinc/ui';
import { createSignal, onCleanup } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { TenantRepository } from '~/repositories/tenantRepository';
import { TransactionsRepository } from '~/repositories/transactionRepository';
import type { TenantFilter, TenantPaymentFilter } from '~/repositories/tenantRepository';
import type { BulkSendTenantPortalInviteDto, SendTenantPortalInviteDto, TenantCreatedDto, UpdateTenantDto } from '~/swagger/portal/Api';

const repo = new TenantRepository();
const transactionsRepo = new TransactionsRepository();

export const [TenantsProvider, useTenants] = createMagicDoorContext('Tenants', () => {
  const { t } = useLocalization();
  const [filtered, setFilter, filteredActions] = createTriggerResource((filter: TenantFilter) =>
    repo.getTenants({ active: true, ...filter })
  );

  const [current, setCurrentId, { trigger: currentId, ...currentActions }] = createTriggerResource((id: string) => repo.getTenant(id));

  const [payments, getPayments, paymentsActions] = createTriggerResource(
    async ({ tenantId, ...filter }: TenantPaymentFilter & { tenantId: string }) => {
      const res = await repo.getPayments(tenantId, filter);
      const result = {
        ...res,
        items: res.items.map((item) => {
          return {
            ...item,
            property: res.properties.find((property) => property.id === item.propertyId),
            unit: res.units.find((unit) => unit.id === item.unitId),
          };
        }),
      };
      return result;
    }
  );

  const [autoPayments, getAutoPayments] = createTriggerResource((tenantId: string) => repo.getAutoPayments(tenantId));

  const [counts, countsActions] = createLazyResource(() => repo.getCounts());

  const addTenant = createMutation(async (model: TenantCreatedDto) => {
    const tenant = await repo.createTenant(model);
    filteredActions.refetch();
    return tenant;
  });

  const updateTenant = createMutation(async (tenantId: string, model: UpdateTenantDto) => {
    const updated = await repo.updateTenant(tenantId, model);
    if (tenantId === currentId()) currentActions.mutate(updated);
    filteredActions.refetch();
    countsActions.refetch();
    return updated;
  });

  /** Unavailable. use deactivateTenant instead in most cases */
  const deleteTenant = createMutation(async (tenantId: string) => {
    await repo.deleteTenant(tenantId);
    if (tenantId === currentId()) currentActions.mutate(undefined);
    filteredActions.refetch();
    countsActions.refetch();
  });

  const deactivateTenant = createMutation(async (tenantId: string, forceMoveOutFromLeases: boolean) => {
    await repo.deactivateTenant(tenantId, forceMoveOutFromLeases);
    if (tenantId === currentId()) currentActions.mutate((prev) => prev && { ...prev, active: false });
    filteredActions.refetch();
    countsActions.refetch();
  });

  const inviteTenant = createMutation(async (tenantId: string, inviteDto: SendTenantPortalInviteDto) => {
    const invite = await repo.portalInvite(tenantId, inviteDto);
    filteredActions.refetch();
    return invite;
  });

  const bulkInviteTenants = createMutation(async (payload: BulkSendTenantPortalInviteDto) => {
    const result = await repo.bulkPortalInvite(payload);
    filteredActions.refetch();
    return result;
  });

  const [isPollingActive, setIsPollingActive] = createSignal(false);
  let previousSendingCount = 0;
  let pollTimeout: ReturnType<typeof setTimeout> | undefined;

  const startInvitePolling = () => {
    if (isPollingActive()) return;
    setIsPollingActive(true);
    previousSendingCount = 0;

    const poll = async () => {
      if (!isPollingActive()) return;
      await countsActions.refetch();
      const sendingCount = counts()?.sendingInvitesCount ?? 0;

      if (sendingCount > 0) {
        previousSendingCount = sendingCount;
        pollTimeout = setTimeout(poll, 3000);
      } else {
        setIsPollingActive(false);
        if (previousSendingCount > 0) {
          filteredActions.refetch();
          toast.success(t('All invites sent successfully'));
        }
      }
    };

    poll();
  };

  onCleanup(() => {
    clearTimeout(pollTimeout);
  });

  const changeTenantPassword = createMutation(async (tenantId: string, password: string) => {
    await repo.changeTenantPassword(tenantId, password);
    filteredActions.refetch();
  });

  const deletePayment = createMutation(async (transactionId: string) => {
    await transactionsRepo.deleteTransaction(transactionId);
    paymentsActions.refetch();
  });

  return {
    filtered,
    setFilter: setFilter,
    current,
    currentActions,
    setCurrentId,
    payments,
    getPayments,
    autoPayments,
    getAutoPayments,
    get counts() {
      countsActions.fetch();
      return counts;
    },
    addTenant,
    updateTenant,
    deleteTenant,
    deactivateTenant,
    inviteTenant,
    bulkInviteTenants,
    startInvitePolling,
    changeTenantPassword,
    deletePayment,
  };
});
